import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { ChurchProfile, ChurchSchedule, HeroSlide } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { COUNTRIES_FIRST, COUNTRIES_REST } from '../../shared/geo/places';
import { useMe } from '../session/SessionProvider';
import { LocationPicker, type LatLng } from '../../shared/ui/LocationPicker';

type SiteData = { slug: string; name: string; profile: ChurchProfile; logoMediaId: string | null; slides: HeroSlide[] };

/**
 * La administración arma el sitio público de la iglesia: identidad, horarios, contacto y carrusel.
 * Un solo «Guardar cambios» fijo abajo guarda todas las tarjetas que cambiaron (la denominación y
 * la transmisión en vivo viven en Configuración de la iglesia).
 */
export function SiteSettingsPage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const base = `/multichurch/churches/${churchId}/site`;
  const data = useAsync((signal) => (churchId ? api.get<SiteData>(base, signal) : Promise.resolve(null)), [base]);
  const [msg, setMsg] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [schedules, setSchedules] = useState<ChurchSchedule[]>([]);
  const [spot, setSpot] = useState<LatLng | null>(null);
  // Qué tarjetas cambiaron: 'site' (Quiénes somos, Horarios, Redes, Contacto) o el id de un destacado.
  const [dirty, setDirty] = useState<ReadonlySet<string>>(new Set());
  const siteForm = useRef<HTMLFormElement>(null);
  const mark = (key: string) => setDirty((d) => (d.has(key) ? d : new Set(d).add(key)));
  // Al guardar se recarga sin quitar la pantalla: el aviso queda junto al botón.
  const last = useRef<SiteData | null>(null);
  if (data.status === 'ready' && data.data) last.current = data.data;
  const loaded = data.status === 'ready' ? data.data : last.current;
  useEffect(() => {
    if (!loaded) return;
    setSchedules(loaded.profile.schedules);
    const { latitude, longitude } = loaded.profile;
    setSpot(latitude != null && longitude != null ? { lat: latitude, lng: longitude } : null);
  }, [loaded]);

  if (!churchId) return <EmptyState title="Elige primero una iglesia en el selector de arriba." />;
  if (data.status === 'loading' && !loaded) return <div className="page"><Loading /></div>;
  if (data.status === 'error' && !loaded) return <div className="page"><ErrorState error={data.error} onRetry={data.reload} /></div>;
  if (!loaded) return null;
  const p = loaded.profile;
  const publicUrl = `${window.location.origin}/i/${loaded.slug}`;

  async function run(action: () => Promise<unknown>, ok: string) {
    setBusy(true);
    setMsg(null);
    try {
      await action();
      setMsg({ tone: 'success', text: ok });
      setDirty(new Set());
      data.reload();
    } catch (e) {
      setMsg({ tone: 'error', text: errorMessage(e) });
    } finally {
      setBusy(false);
    }
  }

  function siteBody(f: FormData): ChurchProfile {
    const t = (k: string) => String(f.get(k) ?? '').trim() || null;
    return {
      tagline: t('tagline'), about: t('about'), denomination: t('denomination'), foundedYear: t('foundedYear') ? Number(t('foundedYear')) : null,
      pastorName: t('pastorName'), phone: t('phone'), whatsapp: t('whatsapp'), email: t('email'), website: t('website'), address: t('address'),
      city: t('city'), state: t('state'), country: t('country'), mapsUrl: t('mapsUrl'), latitude: spot?.lat ?? null, longitude: spot?.lng ?? null, facebookUrl: t('facebookUrl'), instagramUrl: t('instagramUrl'),
      youtubeUrl: t('youtubeUrl'), helpLine: t('helpLine'), primaryColor: t('primaryColor'),
      schedules: schedules.filter((s) => s.label.trim()), listed: f.get('listed') === 'on',
    };
  }

  /** Guarda a la vez todas las tarjetas que cambiaron. */
  function saveAll() {
    const changed = [...dirty];
    void run(async () => {
      for (const key of changed) {
        if (key === 'site') {
          if (siteForm.current) await api.put(base, siteBody(new FormData(siteForm.current)));
        } else {
          const form = document.getElementById(`slide-${key}`);
          if (form instanceof HTMLFormElement) await api.put(`${base}/slides/${key}`, slideBody(new FormData(form)));
        }
      }
    }, 'Cambios guardados. Ya se ven en tu página pública.');
  }
  const editSchedules = (fn: (l: ChurchSchedule[]) => ChurchSchedule[]) => {
    setSchedules(fn);
    mark('site');
  };

  const upload = (path: string, ok: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void run(() => api.upload(`${base}/${path}`, file), ok);
    e.target.value = '';
  };

  return (
    <div className="page">
      <header className="page__header page__header--actions">
        <div>
          <h1>Sitio público</h1>
          <p className="muted">
            Tu página:{' '}
            <a href={publicUrl} target="_blank" rel="noopener noreferrer">
              {publicUrl}
            </a>
          </p>
        </div>
      </header>

      <Alert tone="info">
        La denominación y la transmisión en vivo ahora están en <Link to="/app/configuracion-iglesia">Configuración de la iglesia</Link>.
      </Alert>

      <Card title="Logo">
        {loaded.logoMediaId ? <img className="site-logo-preview" src={`/api/multichurch/public/${loaded.slug}/images/${loaded.logoMediaId}`} alt="Logo actual" /> : <p className="muted small">Sin logo.</p>}
        <div className="field">
          <label htmlFor="logo-file">{loaded.logoMediaId ? 'Cambiar logo' : 'Subir logo'} (JPG, PNG o WebP)</label>
          <input id="logo-file" type="file" accept="image/*" disabled={busy} onChange={upload('logo', 'Logo actualizado.')} />
        </div>
      </Card>

      <form ref={siteForm} className="form" onChange={() => mark('site')} onSubmit={(e) => { e.preventDefault(); saveAll(); }} noValidate>
        <Card title="Quiénes somos">
          <Field label="Frase corta" name="tagline" maxLength={160} defaultValue={p.tagline ?? ''} placeholder="Una familia en Cristo" />
          <div className="field">
            <label htmlFor="site-about">Descripción</label>
            <textarea id="site-about" name="about" rows={4} maxLength={4000} defaultValue={p.about ?? ''} />
          </div>
          <div className="form__row">
            <Field label="Pastor principal" name="pastorName" maxLength={120} defaultValue={p.pastorName ?? ''} />
            <Field label="Denominación" name="denomination" maxLength={120} defaultValue={p.denomination ?? ''} />
          </div>
          <div className="form__row">
            <Field label="Año de fundación" name="foundedYear" type="number" min={1500} max={2100} defaultValue={p.foundedYear ?? ''} />
            <div className="field">
              <label htmlFor="site-color">Color principal</label>
              <input id="site-color" name="primaryColor" type="color" defaultValue={p.primaryColor ?? '#3d7f82'} />
            </div>
          </div>
        </Card>

        <Card title="Horarios">
          {schedules.map((s, i) => (
            <div key={i} className="form__row form__row--3">
              <Field label={`Reunión ${i + 1}`} value={s.label} maxLength={60} onChange={(e) => editSchedules((l) => l.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} placeholder="Culto dominical" />
              <Field label="Día" value={s.day} maxLength={30} onChange={(e) => editSchedules((l) => l.map((x, j) => (j === i ? { ...x, day: e.target.value } : x)))} placeholder="Domingo" />
              <Field label="Hora" value={s.time} maxLength={20} onChange={(e) => editSchedules((l) => l.map((x, j) => (j === i ? { ...x, time: e.target.value } : x)))} placeholder="10:00" />
            </div>
          ))}
          <div className="actions">
            <Button type="button" variant="secondary" onClick={() => editSchedules((l) => [...l, { label: '', day: '', time: '' }])}>
              Agregar horario
            </Button>
            {schedules.length ? (
              <Button type="button" variant="ghost" onClick={() => editSchedules((l) => l.slice(0, -1))}>
                Quitar el último
              </Button>
            ) : null}
          </div>
        </Card>

        <Card title="Redes">
          <p className="small muted">La transmisión en vivo se configura en <Link to="/app/configuracion-iglesia#en-vivo">Configuración de la iglesia</Link>.</p>
          <div className="form__row">
            <Field label="Canal de YouTube" name="youtubeUrl" type="url" maxLength={300} defaultValue={p.youtubeUrl ?? ''} />
            <Field label="Facebook" name="facebookUrl" type="url" maxLength={300} defaultValue={p.facebookUrl ?? ''} />
          </div>
          <div className="form__row">
            <Field label="Instagram" name="instagramUrl" type="url" maxLength={300} defaultValue={p.instagramUrl ?? ''} />
            <Field label="Otro sitio web" name="website" type="url" maxLength={300} defaultValue={p.website ?? ''} />
          </div>
        </Card>

        <Card title="Contacto y ubicación">
          <div className="form__row">
            <Field label="Teléfono" name="phone" type="tel" maxLength={30} defaultValue={p.phone ?? ''} />
            <Field label="WhatsApp" name="whatsapp" type="tel" maxLength={30} defaultValue={p.whatsapp ?? ''} />
          </div>
          <div className="form__row">
            <Field label="Correo" name="email" type="email" maxLength={254} defaultValue={p.email ?? ''} />
            <Field label="Línea de ayuda" name="helpLine" maxLength={120} defaultValue={p.helpLine ?? ''} />
          </div>
          <Field label="Dirección" name="address" maxLength={300} defaultValue={p.address ?? ''} />
          <div className="form__row form__row--3">
            <Field label="Ciudad" name="city" maxLength={120} defaultValue={p.city ?? ''} />
            <Field label="Estado" name="state" maxLength={120} defaultValue={p.state ?? ''} />
            <div className="field">
              <label htmlFor="site-country">País</label>
              <select id="site-country" name="country" defaultValue={p.country ?? 'MX'}>
                {[...COUNTRIES_FIRST, ...COUNTRIES_REST].map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <LocationPicker key={loaded.slug} value={spot} onChange={(v) => { setSpot(v); mark('site'); }} label="Ubicación en el mapa" />
          <Field label="Enlace de Google Maps (opcional)" name="mapsUrl" type="url" maxLength={500} defaultValue={p.mapsUrl ?? ''} placeholder="https://maps.app.goo.gl/…" hint="Si no lo pones, el botón «Cómo llegar» usa el marcador del mapa." />
          <label className="check">
            <input type="checkbox" name="listed" defaultChecked={p.listed} /> Aparecer en «Encuentra tu iglesia»
          </label>
        </Card>
      </form>

      <Card title="Carrusel de portada">
        <p className="small muted">Imágenes grandes con título y botón (por ejemplo, el próximo campamento). Puedes darles fechas para que aparezcan y se quiten solas.</p>
        {loaded.slides.map((s) => (
          <SlideEditor key={s.id} slide={s} slug={loaded.slug} busy={busy} onChange={() => mark(s.id)} onDelete={() => run(() => api.delete(`${base}/slides/${s.id}`), 'Destacado quitado.')} />
        ))}
        <div className="field">
          <label htmlFor="slide-file">Agregar imagen al carrusel</label>
          <input id="slide-file" type="file" accept="image/*" disabled={busy} onChange={upload('slides', 'Imagen agregada. Ponle título y botón si quieres.')} />
        </div>
      </Card>

      {dirty.size || msg ? (
        <div className="sticky-save">
          {msg ? <Alert tone={msg.tone}>{msg.text}</Alert> : null}
          {dirty.size ? (
            <Button busy={busy} className="btn--block" onClick={saveAll}>
              Guardar cambios
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function slideBody(f: FormData): Omit<HeroSlide, 'id' | 'mediaId'> {
  const t = (k: string) => String(f.get(k) ?? '').trim() || null;
  return { title: t('title'), description: t('description'), buttonText: t('buttonText'), buttonUrl: t('buttonUrl'), startsOn: t('startsOn'), endsOn: t('endsOn'), priority: Number(t('priority') ?? 0), active: f.get('active') === 'on' };
}

function SlideEditor({ slide: s, slug, busy, onChange, onDelete }: { slide: HeroSlide; slug: string; busy: boolean; onChange: () => void; onDelete: () => void }) {
  return (
    <form id={`slide-${s.id}`} className="slide-editor" onChange={onChange} onSubmit={(e) => e.preventDefault()}>
      <img src={`/api/multichurch/public/${slug}/images/${s.mediaId}`} alt="" className="slide-editor__img" />
      <div className="slide-editor__fields">
        <Field label="Título" name="title" maxLength={120} defaultValue={s.title ?? ''} />
        <Field label="Texto" name="description" maxLength={300} defaultValue={s.description ?? ''} />
        <div className="form__row">
          <Field label="Texto del botón" name="buttonText" maxLength={40} defaultValue={s.buttonText ?? ''} />
          <Field label="Enlace del botón" name="buttonUrl" maxLength={300} defaultValue={s.buttonUrl ?? ''} placeholder="https://… o /e/…" />
        </div>
        <div className="form__row form__row--3">
          <Field label="Desde" name="startsOn" type="date" defaultValue={s.startsOn ?? ''} />
          <Field label="Hasta" name="endsOn" type="date" defaultValue={s.endsOn ?? ''} />
          <Field label="Prioridad" name="priority" type="number" min={0} max={100} defaultValue={s.priority} />
        </div>
        <label className="check">
          <input type="checkbox" name="active" defaultChecked={s.active} /> Visible
        </label>
        <div className="actions">
          <Button type="button" variant="ghost" disabled={busy} onClick={onDelete}>
            Quitar
          </Button>
        </div>
      </div>
    </form>
  );
}
